import React, { useState, useEffect } from 'react';
import { Button, Card, Tag, message } from 'antd';
import { createPaymentOrder, listPaymentProducts } from '@/services/user';
import { PaymentProduct, ProductType, BillingCycle } from '@/models/common/payment';
import { isWechatBrowser, isMobileBrowser } from '@/utils';
import styles from './index.module.scss';
import { useGlobalStore } from '@/store';

interface SubscriptionProductsProps {
  onSuccess?: () => void;
  selectedProductId?: string;
}

const SubscriptionProducts: React.FC<SubscriptionProductsProps> = ({ 
  onSuccess,
  selectedProductId
}) => {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<PaymentProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<PaymentProduct | null>(null);
  const user = useGlobalStore((state) => state.currentUser);
  const getUserInfo = useGlobalStore((state) => state.getUserInfo);
  const showWechatBindModal = useGlobalStore((state) => state.showWechatBindModal);
  
  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (selectedProductId && products.length > 0) {
      const product = products.find(p => p.publicId === selectedProductId);
      if (product) {
        setSelectedProduct(product);
      }
    }
  }, [selectedProductId, products]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await listPaymentProducts();
      // 只过滤出订阅类型的产品
      const subscriptionProducts = res.data.filter(
        product => product.type === ProductType.SUBSCRIPTION
      );
      setProducts(subscriptionProducts);
    } catch (error) {
      console.error('获取订阅产品失败:', error);
      message.error('获取订阅产品失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProduct = (product: PaymentProduct) => {
    setSelectedProduct(product);
  };

  const handleSubscribe = async () => {
    if (!selectedProduct) return;
    
    let paymentType = 'qrcode';
    
    if (isWechatBrowser()) {
      paymentType = 'jsapi';
      
      // 如果在微信环境但没有关联微信账号，则提示用户
      if (!user?.wechatOpenId) {
        message.error('请先关联微信账号');
        showWechatBindModal?.();
        return;
      }
    } else if (isMobileBrowser()) {
      paymentType = 'h5';
    }
    
    try {
      setLoading(true);
      const res = await createPaymentOrder(selectedProduct.code, paymentType);
      if (!res || !res.data) {
        message.error('创建订阅订单失败');
        return;
      }
      
      const data = res.data;

      if (paymentType === 'jsapi') {
        const { appId, timeStamp, nonceStr, packageValue, signType, paySign } = data;
        if (window.WeixinJSBridge) {
          window.WeixinJSBridge.invoke('getBrandWCPayRequest', {
            appId,
            timeStamp,
            nonceStr,
            package: packageValue,
            signType,
            paySign
          }, function(res) {
            if (res.err_msg === 'get_brand_wcpay_request:ok') {
              handlePaymentSuccess();
            } else {
              message.error('支付失败或取消');
            }
          });
        } else {
          message.error('请在微信客户端打开');
        }
      } else if (paymentType === 'h5') {
        const { mwebUrl, publicId } = data;
        if (mwebUrl) {
          const redirectUrl = `${window.location.origin}/user-center/member`;
          window.location.href = `${mwebUrl}&redirectUrl=${encodeURIComponent(redirectUrl)}`;
          // 支付成功后，会重定向回来，由于页面已刷新，这里不需要处理成功回调
        }
      } else {
        const { qrcodeUrl, publicId } = data;
        if (qrcodeUrl) {
          // 这里需要显示二维码弹窗，由于该组件不包含弹窗逻辑，需要在父组件中处理
          message.info('请使用微信扫码支付');
          // TODO: 在父组件中实现轮询支付结果
        }
      }
    } catch (error) {
      console.error('创建订阅订单失败:', error);
      message.error('创建订阅订单失败');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async () => {
    await getUserInfo();
    message.success('订阅成功');
    onSuccess?.();
  };
  
  const getBillingCycleText = (cycle: BillingCycle) => {
    switch (cycle) {
      case BillingCycle.MONTHLY:
        return '月度';
      case BillingCycle.YEARLY:
        return '年度';
      default:
        return '';
    }
  };

  return (
    <div className={styles.subscriptionProducts}>
      <div className={styles.productList}>
        {products.map(product => (
          <Card
            key={product.publicId}
            className={`${styles.productCard} ${selectedProduct?.publicId === product.publicId ? styles.selected : ''}`}
            onClick={() => handleSelectProduct(product)}
          >
            <div className={styles.productTitle}>{product.title}</div>
            {product.description && (
              <div className={styles.productDescription}>{product.description}</div>
            )}
            <div className={styles.productPrice}>
              <span className={styles.price}>{(product.price / 100).toFixed(2)}</span>
              <span className={styles.unit}>元</span>
              {product.billingCycle && (
                <Tag className={styles.billingCycle}>
                  {getBillingCycleText(product.billingCycle)}
                </Tag>
              )}
            </div>
            {product.features && product.features.length > 0 && (
              <div className={styles.features}>
                {product.features.map((feature, index) => (
                  <div key={index} className={styles.feature}>
                    ✓ {feature}
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>
      
      <div className={styles.actionBar}>
        <Button 
          type="primary" 
          loading={loading} 
          disabled={!selectedProduct} 
          onClick={handleSubscribe}
          block
        >
          立即订阅
        </Button>
      </div>
    </div>
  );
};

export default SubscriptionProducts; 